import RapidBatchSearchApi from 'api/RapidBatchSearchApi';
import PropertyImages from 'components/PropertyImages';
import ResetAttributeButton from 'components/ResetAttributeButton/ResetAttributeButton';
import NumberValidator from 'lib/NumberValidator';
import {isNZLocale} from 'lib/PropertyUtils';
import {isEqual, pick, range} from 'lodash';
import isNaN from 'lodash/isNaN';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import {Container, Form, Grid, Header, Input, Label, Segment} from 'semantic-ui-react';
import {Button} from '@mui/material';
import {grey} from '@mui/material/colors';
import './DataUpdateForm.scss';
import {Info} from '@mui/icons-material';
import SigIconButton from 'components/SigIconButton/SigIconButton';
import {propertyTypeOptions} from 'components/FilterFields/PropertyType';

export const getUpdateObjectFromProperty = (updateProperty = {}) => {
    return {
        propertyType: updateProperty.propertyType ?? '',
        bedrooms: updateProperty.bedrooms ?? 0,
        bathrooms: updateProperty.bathrooms ?? 0,
        carSpaces: updateProperty.carSpaces ?? 0,
        floorArea: updateProperty.floorArea ?? 0,
        landArea: updateProperty.landArea ?? 0,
        capitalValue: updateProperty.capitalValue ?? 0,
    };
};

export const removePDFDisclaimerMark = (content) => {
    return content.replace(/<span.*/, '');
};

const defaultResetFields = () => [
    'propertyType',
    'bedrooms',
    'bathrooms',
    'carSpaces',
    'landArea',
    'floorArea',
];

const PROPERTY_UPDATE_LABELS = {
    propertyType: 'Property Type',
    bedrooms: 'Beds',
    bathrooms: 'Baths',
    carSpaces: 'Cars',
    landArea: 'Land Area',
    floorArea: 'Floor Area',
    imageUri: 'Property Image',
};

const PROPERTY_UPDATE_KEYS = Object.keys(PROPERTY_UPDATE_LABELS);

const numberOptions = range(21).map(i => {
    let option = {};
    option.text = i.toString();
    option.value = i;
    return option;
});

const errorMessages = {
    NUMBER_ERROR: 'Must be a number',
    VALUE_ZERO_TO_20: 'Please enter a value between 0 and 20',
    VALUE_ZERO_TO_HUNDRED_THOUSAND: 'Please enter a value between 0 and 100000',
};

const DataUpdateForm = (
    {
        comparableType,
        featureFlags,
        property,
        targetPropertyId,
        onCancel,
        onDelete,
        onError,
        onUpdateProperty,
    }) => {
    const reader = new FileReader();
    const {propertyType, bedrooms, bathrooms, carSpaces, floorArea, landArea} = property;
    const isNz = isNZLocale();
    const prevPropertyRef = useRef(property);

    const [isLoading, setIsLoading] = useState(false);
    const [isResetLoading, setIsResetLoading] = useState(false);
    const [resetFields, setResetFields] = useState([]);
    const [updateForm, setUpdateForm] = useState(() => {
        let originalProperties = pick(property, PROPERTY_UPDATE_KEYS);
        if (Object.keys(originalProperties).every(k => originalProperties[k] === undefined)) {
            originalProperties = null;
        }

        return {
            originalProperties,
            hasChanged: false,
            propertyType,
            cachedPhotos: [],
            bedrooms,
            bathrooms,
            carSpaces,
            floorArea,
            landArea,
            notifyRpp: true,
        };
    });
    const [formErrors, setFormErrors] = useState({});
    const [fieldFocused, setFieldFocused] = useState('');

    // Uses the original property details to determine what fields were actually updated
    useEffect(() => {
        const changingProps = pick(property, PROPERTY_UPDATE_KEYS);
        const changingPrevProps = pick(prevPropertyRef.current, PROPERTY_UPDATE_KEYS);
        const originalProperties = updateForm.originalProperties;

        if (Object.keys(changingProps).some(k => changingProps[k])) {
            if (!originalProperties) {
                setUpdateForm({...updateForm, originalProperties: changingProps});
            } else if (!isEqual(changingPrevProps, changingProps)) {
                setUpdateForm({
                    ...updateForm,
                    hasChanged: !isEqual(changingProps, originalProperties),
                });
            }
        }
        prevPropertyRef.current = property;
    }, [property, updateForm, featureFlags]);

    function saveForm() {
        setIsLoading(true);
        const {notifyRpp, originalProperties} = updateForm;
        const formWithoutResetFields =
            Object.entries(updateForm)
                .filter(([field]) => !resetFields.includes(field))
                .reduce((acc, [field, value]) => ({
                    ...acc,
                    [field]: value,
                }), {});
        const updatedFields = Object.entries(pick(formWithoutResetFields, PROPERTY_UPDATE_KEYS))
            .filter(([key, value]) => value !== originalProperties[key]);
        const nextProperties = {};
        updatedFields.forEach(([key, value]) => {
            if (key === 'landArea' || key === 'floorArea') {
                const numValue = parseFloat(value);
                nextProperties[key] = isNaN(numValue) ? 0 : numValue;
            } else {
                nextProperties[key] = value;
            }
        });
        const changedKeys = updatedFields
            .map(([k, v]) => ({
                label: PROPERTY_UPDATE_LABELS[k],
                value: v,
            }));

        onUpdateProperty(nextProperties, notifyRpp, changedKeys, resetFields, updateForm.cachedPhotos)
            .catch(() => {
                onError('Failed to send the data update. Please try again.');
            })
            .finally(() => setIsLoading(false));
    }

    function isEditDisabled() {
        return isLoading || isResetLoading;
    }

    function formContainsErrors() {
        return Object.keys(formErrors).length > 0;
    }

    function onAttributeChange(fieldName, maxDigits) {
        return (_, {value}) => {
            const isNumericChange = maxDigits && NumberValidator.isValidInteger(value.toString(), maxDigits);

            if (isNumericChange) {
                const numValue = Number(value);
                if (['bedrooms', 'bathrooms'].includes(fieldName) && (numValue < 0 || numValue > 20)) {
                    setFormErrors({
                        ...formErrors,
                        [fieldName]: errorMessages.VALUE_ZERO_TO_20,
                    });
                } else {
                    removeFormError(fieldName);
                }
            } else if (!isNumericChange && 'propertyType' !== fieldName) {
                setFormErrors({
                    ...formErrors,
                    [fieldName]: errorMessages.NUMBER_ERROR,
                });
                return;
            } else {
                removeFormError(fieldName);
            }

            const field = {};
            field[fieldName] = isNumericChange ? parseInt(value, 10) : value;
            setUpdateForm({...updateForm, ...field});
            setResetFields(resetFields.filter(f => f !== fieldName));
        };
    }

    function onDecimalChange(fieldName) {
        return (_, {value}) => {
            const isLandArea = 'landArea' === fieldName;
            const valueAsNumber = Number.parseFloat(value);
            const isDecimalChange = !isNaN(valueAsNumber);
            const isWithinBounds = isLandArea
                || (valueAsNumber >= 0
                    && valueAsNumber <= 100_000);
            if (value && (!isDecimalChange || !isWithinBounds)) {
                let errorMsg = isDecimalChange
                    ? errorMessages.VALUE_ZERO_TO_HUNDRED_THOUSAND
                    : errorMessages.NUMBER_ERROR;
                setFormErrors({
                    ...formErrors,
                    [fieldName]: errorMsg,
                });
            } else {
                removeFormError(fieldName);
            }
            setUpdateForm({
                ...updateForm,
                [fieldName]: value,
            });
            setResetFields(resetFields.filter(f => f !== fieldName));
        };
    }

    function onFieldFocus(field, event) {
        event.target?.select && event.target.select();
        setFieldFocused(field);
    }

    function onFieldBlur() {
        setFieldFocused('');
    }

    function onNotifyChange(event, data) {
        setUpdateForm({...updateForm, notifyRpp: data.checked});
    }

    function onReset() {
        const {propertyId} = property;
        setIsResetLoading(true);
        RapidBatchSearchApi.getRapidBatchSearch(
            { propertyIds: [propertyId] },
            comparableType || 'sales'
        )
            .then(rapidResponse => {
                const resetProperty = rapidResponse?.data?.[0] ?? {};
                setUpdateForm({
                    ...updateForm,
                    ...getUpdateObjectFromProperty(resetProperty),
                });
                setFormErrors({});
                setResetFields(defaultResetFields());
            })
            .catch(() => {
                onError('Failed to reset the form. Please try again.');
            })
            .finally(() => {
                setUpdateForm((prevForm) => ({
                    ...prevForm,
                    cachedPhotos: [],
                }));
                setIsResetLoading(false);
            });
    }

    function removeFormError(fieldName) {
        const newFormErrors = formErrors;
        delete newFormErrors[fieldName];
        setFormErrors(newFormErrors);
    }

    function renderErrorMsg() {
        if (formErrors[fieldFocused]) {
            const errorLabel = PROPERTY_UPDATE_LABELS[fieldFocused];
            const errorMsg = formErrors[fieldFocused];
            return `${errorLabel}: ${errorMsg}`;
        }
        return null;
    }

    /**
     * Prepare image cache for uploading later
     */
    function onDropAccepted(acceptedFiles = [], eventType) {
        const errorMsg = 'Image not uploaded. Failed to read the file.';
        acceptedFiles.forEach(file => {
            reader.onabort = () => onError(errorMsg);
            reader.onerror = () => onError(errorMsg);
            reader.onload = () => {
                const dataUri = reader.result;
                const newCachedPhoto = {
                    file,
                    dataUri,
                    eventType,
                };
                setUpdateForm((prevForm) => ({
                    ...prevForm,
                    cachedPhotos: [newCachedPhoto, ...prevForm.cachedPhotos],
                }));
            };
            reader.readAsDataURL(file);
        });
    }

    /**
     * Process property detail to pass into property image upload container
     * @returns {{propertyPhotoList: string[]}}
     */
    function getPropertyDetail() {
        const cachedPhotoUris = updateForm.cachedPhotos.map(p => p.dataUri);
        const originalPhotos = property.propertyPhotoList ?? [];
        return {
            ...property,
            propertyPhotoList: [...cachedPhotoUris, ...originalPhotos],
        };
    }

    /**
     * The form's currently cached photos, and any existing already uploaded photos on the property
     * @returns {string[]}
     */
    function getDeletablePhotos() {
        const cachedPhotos = updateForm.cachedPhotos.map(p => p.dataUri);
        const originalDeletablePhotos = property.uploadPhotoList ?? [];
        return [...cachedPhotos, ...originalDeletablePhotos];
    }

    /**
     * Moving the callback here allows the component to control what happens when the image is deleted. We can determine
     * at this time if the user wants to delete an already uploaded image or a cached image, for example
     */
    function onDeleteImage(index) {
        const imageToDelete = getPropertyDetail().propertyPhotoList[index];
        if (imageToDelete) {
            if (imageToDelete.startsWith('data')) {
                setUpdateForm((prevForm) => ({
                    ...prevForm,
                    cachedPhotos: prevForm.cachedPhotos.filter(p => p.dataUri !== imageToDelete),
                }));
            } else {
                onDelete(imageToDelete);
            }
        } else {
            onError('Image not deleted. Failed to access the image.');
        }
    }

    // This should eventually be moved to material UI components
    return (
        <div className="DataUpdateForm" data-testid="DataUpdateForm">
            <Grid>
                <Grid.Row columns={2}>
                    <Grid.Column width={11} className="attribute-fields">
                        <Grid.Row verticalAlign="top">
                            <h2 className="single-line-address">{property.singleLineAddress}</h2>
                        </Grid.Row>
                        <Grid.Row verticalAlign="bottom">
                            <Header className="form-header" as="h4">
                                {isNz ? 'Property Details' : 'Verify the property details'}
                                <SigIconButton
                                    buttonProps={{disabled: true, 'aria-label': 'info-tooltip', sx: { fontSize: '20px' }}}
                                    icon={<Info/>}
                                    title="Your entered property data will be stored and available for use in the current report for 72 hours"
                                    tooltipProps={{
                                        componentsProps: {
                                            tooltip: {
                                                sx: { lineHeight: 'normal' }
                                            }
                                        }
                                    }}
                                />
                            </Header>
                            <Container className="error-container" fluid text textAlign="center">
                                <p className="error-label">{renderErrorMsg()}</p>
                            </Container>
                            <Segment className="form-segment">
                                <Form className="data-update-form-container">
                                    <Form.Group>
                                        <Form.Select id="select-property-type"
                                                     aria-label="Property Type"
                                                     disabled={isEditDisabled()}
                                                     fluid
                                                     width={5}
                                                     className="property-type"
                                                     value={updateForm.propertyType}
                                                     onBlur={onFieldBlur}
                                                     onChange={onAttributeChange('propertyType')}
                                                     onFocus={(event) => onFieldFocus('propertyType', event)}
                                                     label="Property type"
                                                     options={propertyTypeOptions()}/>

                                        <Form.Select id="select-beds"
                                                     aria-label="Beds"
                                                     disabled={isEditDisabled()}
                                                     fluid
                                                     width={3}
                                                     className="beds"
                                                     value={updateForm.bedrooms}
                                                     onBlur={onFieldBlur}
                                                     onChange={onAttributeChange('bedrooms', 2)}
                                                     onFocus={(event) => onFieldFocus('bedrooms', event)}
                                                     label="Beds"
                                                     options={numberOptions}/>

                                        <Form.Select id="select-baths"
                                                     aria-label="Baths"
                                                     disabled={isEditDisabled()}
                                                     fluid
                                                     width={3}
                                                     className="baths"
                                                     value={updateForm.bathrooms}
                                                     onBlur={onFieldBlur}
                                                     onChange={onAttributeChange('bathrooms', 2)}
                                                     onFocus={(event) => onFieldFocus('bathrooms', event)}
                                                     label="Baths"
                                                     options={numberOptions}/>

                                        <Form.Select id="select-cars"
                                                     aria-label="Cars"
                                                     disabled={isEditDisabled()}
                                                     fluid
                                                     width={3}
                                                     className="cars"
                                                     value={updateForm.carSpaces}
                                                     onBlur={onFieldBlur}
                                                     onChange={onAttributeChange('carSpaces', 2)}
                                                     onFocus={(event) => onFieldFocus('carSpaces', event)}
                                                     label="Cars"
                                                     options={numberOptions}/>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Field id="field-land-area"
                                                    disabled={isEditDisabled()}
                                                    error={Boolean(formErrors.landArea)}
                                                    width={4}
                                                    className="land-area"
                                                    data-land-area={updateForm.landArea ?? '0'}>
                                            <label>Land area</label>
                                            <Input
                                                aria-label="Land Area"
                                                disabled={isEditDisabled()}
                                                labelPosition="right"
                                                type="text"
                                                value={updateForm.landArea ?? '0'}
                                                onBlur={onFieldBlur}
                                                onChange={onDecimalChange('landArea')}
                                                onFocus={(event) => onFieldFocus('landArea', event)}>
                                                <input maxLength={8}/>
                                                <Label>m<sup>2</sup></Label>
                                            </Input>
                                        </Form.Field>
                                        <Form.Field id="field-floor-area"
                                                    disabled={isEditDisabled()}
                                                    error={Boolean(formErrors.floorArea)}
                                                    width={4}
                                                    className="floor-area">
                                            <label>Floor area</label>
                                            <Input aria-label="Floor Area"
                                                   disabled={isEditDisabled()}
                                                   labelPosition="right"
                                                   type="text"
                                                   value={updateForm.floorArea ?? '0'}
                                                   onBlur={onFieldBlur}
                                                   onChange={onDecimalChange('floorArea')}
                                                   onFocus={(event) => onFieldFocus('floorArea', event)}>
                                                <input maxLength={6}/>
                                                <Label>m<sup>2</sup></Label>
                                            </Input>
                                        </Form.Field>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Checkbox className="update-rpdata"
                                                       disabled={isEditDisabled()}
                                                       aria-label="Update Cotality"
                                                       checked={updateForm.notifyRpp}
                                                       onChange={onNotifyChange}
                                                       label="Update Cotality"/>
                                    </Form.Group>
                                </Form>
                            </Segment>
                            <Button className="save"
                                    disabled={isEditDisabled() || formContainsErrors()}
                                    onClick={saveForm}
                                    color="primary"
                                    variant="contained"
                                    sx={{mr: 1}}
                                    loading={isLoading}>
                                Save &amp; Close
                            </Button>
                            <Button
                                onClick={onCancel}
                                variant="contained"
                                sx={(theme) => ({
                                    mr: 1,
                                    backgroundColor: grey[300],
                                    color: theme.palette.text.secondary
                                })}
                            >
                                Cancel
                            </Button>
                            <ResetAttributeButton isLoading={isResetLoading}
                                                  disabled={isEditDisabled()}
                                                  title="Click to reset property attributes"
                                                  variant="contained"
                                                  onReset={onReset}/>
                        </Grid.Row>
                    </Grid.Column>
                    <Grid.Column className="images" data-testid="property-images" width={5}>
                        <PropertyImages comparableType={comparableType}
                                        deletableImages={getDeletablePhotos()}
                                        propertyDetail={getPropertyDetail()}
                                        onDelete={onDeleteImage}
                                        onUpload={onDropAccepted}/>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </div>
    );
};

DataUpdateForm.propTypes = {
    comparableType: PropTypes.string,
    featureFlags: PropTypes.shape({
        hideOwnerNamesForVic: PropTypes.bool,
    }),
    property: PropTypes.shape({
        propertyId: PropTypes.number,
        capitalValue: PropTypes.number,
        bedrooms: PropTypes.number,
        bathrooms: PropTypes.number,
        carSpaces: PropTypes.number,
        landArea: PropTypes.number,
        lastSoldPrice: PropTypes.number,
        floorArea: PropTypes.number,
        mapCenter: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        }),
        owners: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        propertyPhotoList: PropTypes.arrayOf(PropTypes.string),
        propertyType: PropTypes.string,
        singleLineAddress: PropTypes.string,
        soldDate: PropTypes.string,
        state: PropTypes.string,
        uploadPhotoList: PropTypes.arrayOf(PropTypes.string),
    }),
    targetPropertyId: PropTypes.number,
    onCancel: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onUpdateProperty: PropTypes.func.isRequired,
};

DataUpdateForm.defaultProps = {
    featureFlags: {},
};

export default DataUpdateForm;
